import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useShop, useI18n } from "@/context/Shop";
import { media } from "@/lib/api";
import { AUDIENCES, audiencePath, brandPath, catPath, childrenFor, collectionPath, deptCount, deptPath } from "@/lib/nav";
import { CurrencyToggle, LanguageToggle } from "@/components/store/Toggles";

const FALLBACK_TILE = "https://images.unsplash.com/photo-1529408570047-e4414fb17e95";

/* ------------------------------------------------------------------ mega menu panels */
function AudiencePanel({ audience, onClose }) {
  const { nav } = useShop();
  const { t, tr } = useI18n();
  const depts = (nav?.departments || []).filter((d) => deptCount(d, audience) > 0);
  const aud = (nav?.audiences || []).find((a) => a.key === audience);
  return (
    <div className="container-lux grid grid-cols-12 gap-8 py-10">
      {depts.map((d) => {
        const kids = childrenFor(d, audience);
        return (
          <div key={d.slug} className="col-span-3" data-testid={`mega-${audience}-${d.slug}`}>
            <Link to={deptPath(d.slug, audience)} onClick={onClose} className="eyebrow text-foreground hover:underline underline-offset-4 inline-block mb-4" data-testid={`mega-${audience}-${d.slug}-link`}>{tr(d)}</Link>
            <ul className="space-y-2">
              {kids.slice(0, 11).map((c) => (
                <li key={c.slug}><Link to={catPath(d.slug, c.slug, audience)} onClick={onClose} className="text-[13px] text-foreground/80 hover:text-foreground hover:underline underline-offset-4" data-testid={`mega-${audience}-${d.slug}-${c.slug}-link`}>{tr(c)}</Link></li>
              ))}
              <li className="pt-1"><Link to={deptPath(d.slug, audience)} onClick={onClose} className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux">{t("shop_all")} · {deptCount(d, audience)}</Link></li>
            </ul>
          </div>
        );
      })}
      <Link to={`${audiencePath(audience)}?new=true`} onClick={onClose} className={`${depts.length >= 3 ? "col-span-3" : "col-span-6"} relative overflow-hidden group bg-muted min-h-[280px]`} data-testid={`mega-${audience}-editorial-link`}>
        <img src={media(aud?.image_url || FALLBACK_TILE, 900)} alt="" className="absolute inset-0 w-full h-full object-cover img-zoom" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 text-white">
          <p className="text-[11px] uppercase tracking-[0.18em] opacity-90">{t(audience)}</p>
          <p className="font-display text-2xl mt-1">{t("new_arrivals")}</p>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] mt-3 border-b border-white/70 pb-0.5">{t("discover")} <ArrowRight className="h-3.5 w-3.5" /></span>
        </div>
      </Link>
    </div>
  );
}

function BrandsPanel({ onClose }) {
  const { nav } = useShop();
  const { t } = useI18n();
  const brands = nav?.brands || [];
  const featured = brands.filter((b) => b.featured);
  const cols = 3;
  const per = Math.ceil(brands.length / cols) || 1;
  return (
    <div className="container-lux grid grid-cols-12 gap-8 py-10">
      <div className="col-span-8 grid grid-cols-3 gap-8">
        {Array.from({ length: cols }).map((_, i) => (
          <ul key={i} className="space-y-2.5">
            {brands.slice(i * per, (i + 1) * per).map((b) => (
              <li key={b.slug}><Link to={brandPath(b.slug)} onClick={onClose} className={`text-[13px] hover:underline underline-offset-4 ${b.has_products ? "text-foreground/85 hover:text-foreground" : "text-muted-foreground"}`} data-testid={`mega-brand-${b.slug}-link`}>{b.name}</Link></li>
            ))}
          </ul>
        ))}
        <div className="col-span-3 pt-2"><Link to="/brands" onClick={onClose} className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux" data-testid="mega-all-brands-link">{t("all_brands")}</Link></div>
      </div>
      <div className="col-span-4">
        <p className="eyebrow mb-4">{t("featured")}</p>
        <ul className="space-y-2.5">
          {featured.slice(0, 6).map((b) => <li key={b.slug}><Link to={brandPath(b.slug)} onClick={onClose} className="font-display text-xl hover:underline underline-offset-4">{b.name}</Link></li>)}
        </ul>
      </div>
    </div>
  );
}

function CollectionsPanel({ onClose }) {
  const { nav } = useShop();
  const { t, tr } = useI18n();
  const cols = nav?.collections || [];
  return (
    <div className="container-lux py-10">
      <div className="grid grid-cols-5 gap-6">
        {cols.slice(0, 5).map((c) => (
          <Link key={c.slug} to={collectionPath(c.slug)} onClick={onClose} className="group block" data-testid={`mega-collection-${c.slug}-link`}>
            <div className="aspect-[4/3] overflow-hidden bg-muted">{c.hero_image_url && <img src={media(c.hero_image_url, 600)} alt="" className="w-full h-full object-cover img-zoom" loading="lazy" />}</div>
            <p className="font-display text-lg mt-3 leading-tight">{tr(c)}</p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">{c.gender === "all" ? t("for_everyone") : t(c.gender)}</p>
          </Link>
        ))}
      </div>
      <Link to="/collections" onClick={onClose} className="inline-block mt-8 text-[12px] uppercase tracking-[0.14em] font-semibold link-lux">{t("view_all")}</Link>
    </div>
  );
}

/* ------------------------------------------------------------------ header */
export function Header() {
  const { cart, wishlist, setBagOpen, setSearchOpen, customer, config, nav } = useShop();
  const { t, tr } = useI18n();
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const closeTimer = useRef(null);

  useEffect(() => {
    setOpen(null);
    setMobile(false);
  }, [loc.pathname, loc.search]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  const openPanel = (key) => {
    cancelClose();
    setOpen(key);
  };

  const items = [
    { key: "new", to: "/new-arrivals", label: t("new_arrivals"), id: "nav-new-arrivals-link" },
    ...AUDIENCES.map((a) => ({ key: a, to: audiencePath(a), label: t(a), id: `nav-${a}-link`, mega: true })),
    { key: "brands", to: "/brands", label: t("brands"), id: "nav-brands-link", mega: true },
    { key: "collections", to: "/collections", label: t("collections"), id: "nav-collections-link", mega: true },
    { key: "sale", to: "/sale", label: t("sale"), id: "nav-sale-link", sale: true },
  ];
  const storeName = config?.store_name || "EA Italian Luxury Brands";

  return (
    <header data-testid="site-header">
      {/* utility bar */}
      <div className="bg-[hsl(var(--header-bg))] text-[hsl(var(--header-muted))] border-b border-white/10">
        <div className="container-lux flex items-center justify-between h-9 text-[11px] uppercase tracking-[0.12em]">
          <div className="flex items-center gap-5">
            <Link to="/customer-care" className="hover:text-white transition-colors" data-testid="utility-customer-care-link">{t("customer_care")}</Link>
            <Link to="/customer-care/track-order" className="hidden sm:inline hover:text-white transition-colors" data-testid="utility-track-order-link">{t("track_order")}</Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline" data-testid="utility-delivery-note">{t("delivery_al")}</span>
            <LanguageToggle />
            <CurrencyToggle />
          </div>
        </div>
      </div>

      {/* sticky black header */}
      <div className="sticky top-0 z-50 bg-[hsl(var(--header-bg))] text-[hsl(var(--header-fg))]" onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
        <div className="container-lux h-16 grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center gap-1">
            <button className="lg:hidden -ml-2 p-2 hover:bg-white/10 rounded-none" aria-label={t("menu")} onClick={() => setMobile(true)} data-testid="mobile-nav-open-button"><Menu className="h-5 w-5" strokeWidth={1.6} /></button>
            <button aria-label={t("search")} className="inline-flex items-center gap-2 p-2 -ml-2 lg:ml-0 hover:bg-white/10 transition-colors" onClick={() => setSearchOpen(true)} data-testid="search-open-button">
              <Search className="h-[19px] w-[19px]" strokeWidth={1.6} />
              <span className="hidden lg:inline text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--header-muted))]">{t("search")}</span>
            </button>
          </div>
          <Link to="/" className="justify-self-center text-center px-2" data-testid="site-logo-link" aria-label={storeName}>
            <span className="wordmark block whitespace-nowrap text-[10px] tracking-[0.22em] sm:text-[13px] sm:tracking-[0.28em] leading-none">{storeName}</span>
          </Link>
          <div className="flex items-center justify-end gap-0.5 sm:gap-1">
            <button aria-label={t("account")} className="p-2 hover:bg-white/10 transition-colors hidden sm:inline-flex" onClick={() => navigate(customer ? "/account" : "/account/login")} data-testid="header-account-button"><User className="h-[19px] w-[19px]" strokeWidth={1.6} /></button>
            <Link aria-label={t("wishlist")} to="/wishlist" className="relative p-2 hover:bg-white/10 transition-colors" data-testid="header-wishlist-button">
              <Heart className="h-[19px] w-[19px]" strokeWidth={1.6} />
              {wishlist.count > 0 && <span className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-white text-black text-[10px] font-semibold grid place-items-center tabular" data-testid="header-wishlist-count">{wishlist.count}</span>}
            </Link>
            <button aria-label={t("bag")} className="relative p-2 -mr-2 hover:bg-white/10 transition-colors" onClick={() => setBagOpen(true)} data-testid="bag-open-button">
              <ShoppingBag className="h-[19px] w-[19px]" strokeWidth={1.6} />
              {cart.item_count > 0 && <span className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-white text-black text-[10px] font-semibold grid place-items-center tabular" data-testid="header-bag-count">{cart.item_count}</span>}
            </button>
          </div>
        </div>

        <nav className="hidden lg:block border-t border-white/10" aria-label="Primary">
          <ul className="container-lux flex items-center justify-center gap-9 h-11">
            {items.map((l) => (
              <li key={l.key} onMouseEnter={() => (l.mega ? openPanel(l.key) : scheduleClose())}>
                <NavLink to={l.to} data-testid={l.id} onFocus={() => (l.mega ? openPanel(l.key) : setOpen(null))}
                  className={({ isActive }) => `nav-link underline-grow py-3 inline-block transition-colors ${l.sale ? "text-[hsl(var(--sale-on-dark))]" : isActive || open === l.key ? "text-white" : "text-white/80 hover:text-white"}`}
                  data-active={open === l.key ? "true" : undefined}>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {open && (
          <div className="absolute left-0 right-0 top-full bg-background text-foreground border-t border-border shadow-[0_18px_40px_hsl(var(--shadow-2))] hidden lg:block" onMouseEnter={cancelClose} onMouseLeave={scheduleClose} data-testid={`mega-menu-${open}`}>
            {AUDIENCES.includes(open) && <AudiencePanel audience={open} onClose={() => setOpen(null)} />}
            {open === "brands" && <BrandsPanel onClose={() => setOpen(null)} />}
            {open === "collections" && <CollectionsPanel onClose={() => setOpen(null)} />}
          </div>
        )}
      </div>

      {/* mobile navigation */}
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-full sm:max-w-md bg-background p-0 flex flex-col [&>button]:hidden" data-testid="mobile-nav-sheet">
          <SheetHeader className="bg-[hsl(var(--header-bg))] text-[hsl(var(--header-fg))] px-5 h-16 flex flex-row items-center justify-between space-y-0">
            <SheetTitle className="wordmark text-[hsl(var(--header-fg))] text-[11px] tracking-[0.24em] text-left font-sans">{storeName}</SheetTitle>
            <button className="p-2 -mr-2 hover:bg-white/10" aria-label={t("close")} onClick={() => setMobile(false)} data-testid="mobile-nav-close-button"><X className="h-5 w-5" /></button>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-3 border-b">
              <Link to="/new-arrivals" className="nav-link block py-3" data-testid="mobile-nav-new-arrivals-link">{t("new_arrivals")}</Link>
            </div>
            <Accordion type="single" collapsible className="px-5" data-testid="mobile-nav-department-accordion">
              {AUDIENCES.map((a) => (
                <AccordionItem key={a} value={a}>
                  <AccordionTrigger className="nav-link hover:no-underline py-4" data-testid={`mobile-nav-${a}-trigger`}>{t(a)}</AccordionTrigger>
                  <AccordionContent>
                    <Link to={audiencePath(a)} className="block text-[12px] uppercase tracking-[0.14em] font-semibold py-2 link-lux" data-testid={`mobile-nav-${a}-all-link`}>{t("shop_all")} · {t(a)}</Link>
                    {(nav?.departments || []).filter((d) => deptCount(d, a) > 0).map((d) => (
                      <div key={d.slug} className="mt-4">
                        <Link to={deptPath(d.slug, a)} className="eyebrow text-foreground block mb-2">{tr(d)}</Link>
                        <ul className="grid grid-cols-2 gap-x-4">
                          {childrenFor(d, a).map((c) => <li key={c.slug}><Link to={catPath(d.slug, c.slug, a)} className="block py-1.5 text-sm text-foreground/80">{tr(c)}</Link></li>)}
                        </ul>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
              <AccordionItem value="brands">
                <AccordionTrigger className="nav-link hover:no-underline py-4" data-testid="mobile-nav-brands-trigger">{t("brands")}</AccordionTrigger>
                <AccordionContent>
                  <ul className="grid grid-cols-2 gap-x-4">{(nav?.brands || []).map((b) => <li key={b.slug}><Link to={brandPath(b.slug)} className="block py-1.5 text-sm text-foreground/80">{b.name}</Link></li>)}</ul>
                  <Link to="/brands" className="block text-[12px] uppercase tracking-[0.14em] font-semibold py-3 link-lux">{t("all_brands")}</Link>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="px-5 py-3 border-t">
              <Link to="/collections" className="nav-link block py-3" data-testid="mobile-nav-collections-link">{t("collections")}</Link>
              <Link to="/sale" className="nav-link block py-3 text-[hsl(var(--sale))]" data-testid="mobile-nav-sale-link">{t("sale")}</Link>
            </div>
            <div className="px-5 py-4 border-t space-y-3 text-sm">
              <Link className="block" to={customer ? "/account" : "/account/login"} data-testid="mobile-nav-account-link">{customer ? t("my_account") : t("sign_in")}</Link>
              <Link className="block" to="/customer-care">{t("customer_care")}</Link>
              <Link className="block" to="/customer-care/track-order">{t("track_order")}</Link>
              <Link className="block" to="/about">{t("about")}</Link>
              <Link className="block" to="/customer-care/contact">{t("contact")}</Link>
            </div>
          </div>
          <div className="border-t px-5 py-4 flex items-center justify-between bg-secondary">
            <LanguageToggle dark={false} />
            <CurrencyToggle dark={false} />
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
